/**
 * Pure, client-safe area links (spec section 5.2). Reads only the slim route index, never the
 * full catalogue, so client components (the spot hero, board chips) can use it.
 */
import { conditionsBoardHref } from "@/lib/seo/conditions-links";
import { AREA_ROUTE_INDEX } from "./route-index.generated";
import type { AreaPageSport } from "./types";

/** "/conditions/surf/cornwall". */
export function areaPagePath(areaSlug: string, sport: AreaPageSport): string {
  return `/conditions/${sport}/${areaSlug}`;
}

const OWN_BOARD = new Set([
  "off-piste", "touring", "mtb", "surf", "bodyboarding", "wind", "foil", "downwind-foil", "wing-foil", "diving",
]);

/**
 * The board for a sport key: "/conditions/piste", "/conditions/open-water", "/conditions/sailing",
 * the generic SUP board for named profiles, and "/conditions" for anything unknown.
 */
export function boardPathForSport(sport: string): string {
  if (sport === "piste" || sport === "ski") return "/conditions/piste";
  if (OWN_BOARD.has(sport)) return `/conditions/${sport}`;
  if (sport === "swimming" || sport === "swim" || sport === "open-water") return "/conditions/open-water";
  if (sport === "sup" || sport === "sup-touring" || sport === "sup-surf" || sport === "sup-downwind") return "/conditions/sup";
  if (sport === "sailing" || sport === "sailing-club" || sport === "sailing-hp") return "/conditions/sailing";
  return "/conditions";
}

/** The area page sport a board sport key maps to, if any. */
export function areaPageSportFor(sport: string): AreaPageSport | null {
  if (sport === "surf") return "surf";
  if (sport === "piste" || sport === "ski") return "piste";
  return null;
}

/** The area itself or its nearest ancestor with a page for the sport, from the route index. */
export function pageAreaSlugFor(areaSlug: string, sport: AreaPageSport): string | null {
  const seen = new Set<string>();
  let current: string | null = areaSlug;
  while (current && !seen.has(current)) {
    seen.add(current);
    const entry: (typeof AREA_ROUTE_INDEX)[string] | undefined = AREA_ROUTE_INDEX[current];
    if (!entry) return null;
    if (entry.pageSports.includes(sport)) return current;
    current = entry.parent;
  }
  return null;
}

/**
 * The area page when one exists for the sport (self or an ancestor), else the sport's board
 * filtered to the area through the fragment ("/conditions/wind#area=cornwall").
 */
export function areaLinkForSport(areaSlug: string, sport: string): string {
  const pageSport = areaPageSportFor(sport);
  if (pageSport) {
    const pageArea = pageAreaSlugFor(areaSlug, pageSport);
    if (pageArea) return areaPagePath(pageArea, pageSport);
  }
  const board = boardPathForSport(sport);
  if (board === "/conditions" || !Object.hasOwn(AREA_ROUTE_INDEX, areaSlug)) return board;
  return conditionsBoardHref(board, { area: areaSlug });
}
